import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useNavigate } from "react-router-dom";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { useUser, useSession } from "@clerk/clerk-react";
import NotificationModal from "../../components/NotificationModal";
import {
  setMfaSetupDeferredForCurrentClerkSession,
  clearMfaSetupSessionDeferral,
} from "../../utils/mfaSetupSessionDeferral";
import { useUpdateProviderUserMfaStatus } from "../../custom-hooks/queries";
import {
  isOrgMfaEnabled,
  isPublicMetadataTwoFactorEnabled,
  isClerkTotpEnrolled,
} from "../../utils/mfaClerkPublicMetadata";

const schema = yup.object().shape({
  current_password: yup.string().required("Current password is required"),
  new_password: yup
    .string()
    .required("New password is required")
    .min(8, "Password must be at least 8 characters")
    .matches(/[A-Z]/, "Must include an uppercase letter")
    .matches(/[a-z]/, "Must include a lowercase letter")
    .matches(/[0-9]/, "Must include a number")
    .matches(/[^A-Za-z0-9]/, "Must include a symbol"),
  confirm_password: yup
    .string()
    .required("Confirm password is required")
    .oneOf([yup.ref("new_password")], "Passwords must match"),
});

const ManageProviderUser = () => {
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const [mfaBusy, setMfaBusy] = useState(false);
  const { user } = useUser();
  const { session } = useSession();

  const orgMfaRequired = isOrgMfaEnabled(user);

  const { mutateAsync: patchProviderMfaStatus } =
    useUpdateProviderUserMfaStatus();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (watch("confirm_password")?.length > 0) {
      setPasswordsMatch(watch("new_password") === watch("confirm_password"));
    } else {
      setPasswordsMatch(true);
    }
  }, [watch("new_password"), watch("confirm_password")]);

  const mfaChecked =
    orgMfaRequired ||
    isClerkTotpEnrolled(user) ||
    isPublicMetadataTwoFactorEnabled(user);

  /**
   * TOTP only on /mfa/setup. PATCH syncs Clerk publicMetadata; optional redirect deferral
   * via mfaSetupSessionDeferral.
   */
  const handleMfaToggle = async (nextChecked) => {
    if (orgMfaRequired || mfaBusy) return;
    setMfaBusy(true);
    try {
      if (nextChecked) {
        await patchProviderMfaStatus({
          two_factor_enabled: true,
          mfa_prompt_seen: false,
        });
        setMfaSetupDeferredForCurrentClerkSession(session?.id);
        await user?.reload?.();
        setMessage(
          "MFA enrollment will start after your next sign-in. You can keep using the app until then."
        );
        setIsError(false);
        setShow(true);
      } else {
        clearMfaSetupSessionDeferral();
        if (typeof user?.disableTOTP === "function" && user?.twoFactorEnabled) {
          await user.disableTOTP();
        }
        await patchProviderMfaStatus({
          two_factor_enabled: false,
          mfa_prompt_seen: true,
        });
        await user?.reload?.();
        setMessage("Multi-factor authentication has been turned off.");
        setIsError(false);
        setShow(true);
      }
    } catch (error) {
      const clerkMsg = error?.errors?.[0]?.message;
      setMessage(
        clerkMsg ||
          error?.response?.data?.message ||
          error?.message ||
          "Failed to update MFA. Please try again."
      );
      setIsError(true);
      setShow(true);
    } finally {
      setMfaBusy(false);
    }
  };

  const onSubmit = async (data) => {
    setIsSubmitting(true);

    try {
      await user?.updatePassword({
        currentPassword: data.current_password,
        newPassword: data.new_password,
        signOutOfOtherSessions: true,
      });

      setMessage("Password updated successfully");
      setIsError(false);
      setShow(true);
    } catch (error) {
      setMessage(
        error.response?.data?.message ||
          "Failed to update password. Please try again."
      );
      setIsError(true);
      setShow(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  // We don't need loading state anymore as we're only handling passwords

  return (
    <div className="w-full px-5">
      <div className="mt-6 pb-5 space-y-3 border-b border-gray-200 px-0 md:px-1">
        <h2 className="text-lg font-semibold">Change Password</h2>
        <p className="text-sm text-gray-600">Update your password below.</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="w-full">
        <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6 mt-4 border-b border-gray-300 py-5 lg:py-6">
          <>
            <label className="block text-sm font-medium text-gray-900 sm:pt-1.5">
              Current Password
            </label>
            <div className="mt-2 sm:col-span-2 sm:mt-0 relative w-[300px] lg:w-[400px] xl:w-[500px]">
              <input
                {...register("current_password")}
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                name="current_password"
                placeholder="Enter your current password"
                className="ring-0 border border-gray-300 rounded-md text-sm w-full pr-10 bg-white"
              />
              <div
                className="absolute inset-y-0 right-2 flex items-center cursor-pointer"
                onClick={() => setShowPassword((prev) => !prev)}
              >
                {showPassword ? (
                  <EyeSlashIcon className="h-5 w-5 text-gray-500" />
                ) : (
                  <EyeIcon className="h-5 w-5 text-gray-500" />
                )}
              </div>
              {errors.current_password && (
                <p className="mt-2 text-sm text-red-600">
                  {errors.current_password.message}
                </p>
              )}
            </div>
          </>
          <>
            <label className="block text-sm font-medium text-gray-900 sm:pt-1.5">
              New Password
            </label>
            <div className="mt-2 sm:col-span-2 sm:mt-0 relative w-[300px] lg:w-[400px] xl:w-[500px]">
              <input
                {...register("new_password")}
                type={showNewPassword ? "text" : "password"}
                autoComplete="new-password"
                name="new_password"
                placeholder="Must include uppercase, lowercase, number & symbol (min 8 characters)"
                className="ring-0 border border-gray-300 rounded-md text-sm w-full pr-10"
              />
              <div
                className="absolute inset-y-0 right-2 flex items-center pointer-events-auto"
                style={{ height: "2.5rem" }}
              >
                <span
                  className="flex items-center cursor-pointer h-full"
                  onClick={() => setShowNewPassword((prev) => !prev)}
                  tabIndex={0}
                  role="button"
                  aria-label={
                    showNewPassword ? "Hide password" : "Show password"
                  }
                >
                  {showNewPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-gray-500" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-500" />
                  )}
                </span>
              </div>
              {errors.new_password && (
                <p className="mt-2 text-sm text-red-600">
                  {errors.new_password.message}
                </p>
              )}
            </div>
          </>
          <>
            <label className="block text-sm font-medium text-gray-900 sm:pt-1.5">
              Confirm Password
            </label>
            <div className="mt-2 sm:col-span-2 sm:mt-0 relative w-[300px] lg:w-[400px] xl:w-[500px]">
              <input
                {...register("confirm_password")}
                type={showConfirmPassword ? "text" : "password"}
                autoComplete="new-password"
                name="confirm_password"
                placeholder="Confirm your password"
                className="ring-0 border border-gray-300 rounded-md text-sm w-full pr-10"
              />
              <div
                className="absolute inset-y-0 right-2 flex items-center pointer-events-auto"
                style={{ height: "2.5rem" }}
              >
                <span
                  className="flex items-center cursor-pointer h-full"
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                  tabIndex={0}
                  role="button"
                  aria-label={
                    showConfirmPassword ? "Hide password" : "Show password"
                  }
                >
                  {showConfirmPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-gray-500" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-500" />
                  )}
                </span>
              </div>
              {errors.confirm_password && (
                <p className="mt-2 text-sm text-red-600">
                  {errors.confirm_password.message}
                </p>
              )}
            </div>
          </>
        </div>

        {/* Security (MFA) — org_mfa_enabled / two_factor_enabled in Clerk publicMetadata */}
        <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6 mt-4 border-b border-gray-300 py-5 lg:py-6">
          <label className="block text-sm font-medium text-gray-900 sm:pt-1.5">
            Security
          </label>
          <div className="mt-2 sm:col-span-2 sm:mt-0 w-[300px] lg:w-[400px] xl:w-[500px]">
            <fieldset>
              <legend className="sr-only">Security settings</legend>
              <p className="text-sm text-gray-600 mb-4">
                Manage the security settings for your account.
              </p>
              <div className="flex items-start">
                <div className="flex h-6 shrink-0 items-center">
                  <div
                    className={`group grid size-4 grid-cols-1 ${orgMfaRequired ? "opacity-60" : ""
                      }`}
                  >
                    <input
                      id="mfa-enabled-provider"
                      type="checkbox"
                      checked={mfaChecked}
                      disabled={orgMfaRequired || mfaBusy}
                      onChange={(e) => handleMfaToggle(e.target.checked)}
                      className="col-start-1 row-start-1 size-4 appearance-none rounded-sm border border-gray-300 bg-white checked:border-indigo-600 checked:bg-indigo-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed"
                    />
                    <svg
                      viewBox="0 0 16 16"
                      fill="white"
                      className={`pointer-events-none col-start-1 row-start-1 size-4 ${mfaChecked ? "opacity-100" : "opacity-0"
                        }`}
                      aria-hidden
                    >
                      <path d="M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-3 min-w-0">
                  <label
                    htmlFor="mfa-enabled-provider"
                    className={`block text-sm font-medium ${orgMfaRequired ? "text-gray-500" : "text-gray-900"
                      }`}
                  >
                    Multi-Factor Authentication
                  </label>
                  <p className="mt-1 text-xs text-gray-500">
                    {orgMfaRequired
                      ? "Required by your organization"
                      : "Require a second factor when signing in to your account."}
                  </p>
                </div>
              </div>
            </fieldset>
          </div>
        </div>

        {/* Submit Button */}
        <div className="mt-10 flex items-center justify-end gap-x-6 py-2 px-5">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex justify-center rounded-md px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm border border-gray-300 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm bg-indigo-600 hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:bg-indigo-400 disabled:cursor-not-allowed"
          >
            {isSubmitting ? "Updating..." : "Update Password"}
          </button>
        </div>
      </form>

      <NotificationModal
        open={show}
        setOpen={setShow}
        desc={message}
        title={isError ? "Error" : "Success"}
        error={isError}
        autoDismiss={!isError}
      />
    </div>
  );
};

export default ManageProviderUser;
